import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Stack,
  Box,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import { FormProvider, RHFAutocomplete } from "@app/components";
import { Strings } from "@app/constants";

interface Props {
  open?: boolean;
  onClose?: VoidFunction;
  row?: { lat?: number; lng?: number };
  onSave?: (data: {
    name?: string;
    address?: string;
    lat: number;
    lng: number;
    zipCode: string;
    city?: string;
    country?: string;
  }) => void;
}

const MapModal = ({ open = false, onClose, row, onSave }: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const defaultCenter = { lat: 28.6139, lng: 77.209 };
  const [markerPosition, setMarkerPosition] = useState(defaultCenter);
  const [suggestions, setSuggestions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [selectedPlace, setSelectedPlace] = useState<any>(null);

  const methods = useForm();

  useEffect(() => {
    if (!open) {
      setMarkerPosition(defaultCenter);
      setSuggestions([]);
      setSelectedPlace(null);
      methods.reset();
    }
  }, [open]);

  const handleMarkerDragEnd = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      setMarkerPosition({
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      });
    }
  };

  const fetchAutocompleteSuggestions = (query: string) => {
    const service = new window.google.maps.places.AutocompleteService();
    service.getPlacePredictions({ input: query }, (predictions, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        setSuggestions(predictions || []);
      } else {
        setSuggestions([]);
      }
    });
  };

  const handleSuggestionClick = (placeId: string) => {
    const service = new window.google.maps.places.PlacesService(
      document.createElement("div")
    );

    service.getDetails({ placeId }, (place, status) => {
      if (
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        place?.geometry
      ) {
        const location = place?.geometry.location;

        const getAddressComponent = (type: string) =>
          place?.address_components?.find((c) => c.types.includes(type))
            ?.long_name || "";
        let zipCode = getAddressComponent("postal_code");
        const city =
          getAddressComponent("locality") ||
          getAddressComponent("administrative_area_level_2") ||
          getAddressComponent("administrative_area_level_1");
        const country = getAddressComponent("country");

        setMarkerPosition({
          lat: location?.lat() ?? defaultCenter.lat,
          lng: location?.lng() ?? defaultCenter.lng,
        });
        if (!zipCode) {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode(
            { location: { lat: location?.lat() ?? defaultCenter.lat, lng: location?.lng() ?? defaultCenter.lng } },
            (results, status) => {
              if (status === "OK" && results?.length) {
                const addressComponents = results[0].address_components;

                const getFromGeo = (type: string) =>
                  addressComponents.find((c) => c.types.includes(type))
                    ?.long_name || "";

                zipCode = getFromGeo("postal_code");

                setSelectedPlace({
                  ...place,
                  zipCode,
                  city,
                  country,
                });

                methods.setValue(
                  "searchLocation",
                  place?.formatted_address || ""
                );
              }
            }
          );
        } else {
          setSelectedPlace({
            ...place,
            zipCode,
            city,
            country,
          });

          methods.setValue("searchLocation", place.formatted_address || "");
        }
      }
    });
  };

  return (
    <FormProvider methods={methods}>
      <LoadScript
        googleMapsApiKey="AIzaSyBnawHBTXW1VW3cyIV2f2RY5Z_SrzX2dWU"
        libraries={["places"]}
      >
        <Dialog
          open={open}
          onClose={onClose}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: 3,
              width: isMobile ? "90vw" : "100%",
              mx: isMobile ? 2 : 0,
            },
          }}
        >
          <DialogTitle>
            <Typography fontWeight={600} fontSize={isMobile ? 18 : 20}>
              {Strings.common.addNewLocation}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <Icon icon="mdi:close" width="24" height="24" color="#000" />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <Stack spacing={2} mt={2} mb={3}>
              <RHFAutocomplete
                name="searchLocation"
                label="Search Location"
                options={suggestions.map((s) => s.description)}
                onInputChange={(e, value) =>
                  fetchAutocompleteSuggestions(value)
                }
                onChange={(e, value) => {
                  const selected = suggestions.find(
                    (s) => s.description === value
                  );
                  if (selected) handleSuggestionClick(selected.place_id);
                }}
              />

              <Box
                sx={{
                  width: "100%",
                  borderRadius: 2,
                  overflow: "hidden",
                  mb: 2,
                }}
              >
                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "250px" }}
                  center={markerPosition}
                  zoom={14}
                  key={`${markerPosition.lat}-${markerPosition.lng}`}
                >
                  <Marker
                    position={markerPosition}
                    draggable
                    onDragEnd={handleMarkerDragEnd}
                  />
                </GoogleMap>
              </Box>

              <Box textAlign="right">
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    const name = selectedPlace?.name || "";
                    const address = selectedPlace?.formatted_address || "";
                    const zipCode = selectedPlace?.zipCode || "";
                    const city = selectedPlace?.city || "";
                    const country = selectedPlace?.country || "";
                    const { lat, lng } = markerPosition;

                    onClose?.();
                    onSave?.({
                      name,
                      address,
                      lat,
                      lng,
                      zipCode,
                      city,
                      country,
                    });
                  }}
                  sx={{
                    fontWeight: 600,
                    fontSize: isMobile ? 14 : 16,
                    py: 1.2,
                    backgroundColor: theme.palette.primary.main,
                    maxWidth: 150,
                    color: theme.palette.common.white,
                    mb: 2,
                  }}
                >
                  {Strings.button.save}
                </Button>
              </Box>
            </Stack>
          </DialogContent>
        </Dialog>
      </LoadScript>
    </FormProvider>
  );
};

export { MapModal };
